import { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { APP_ORIGIN } from '../../lib/domain';
import logoUrl from '../../src/styles/logo.png';
import {
  ArrowRight,
  ChevronRight, Menu, X, Sparkles, Check,
} from 'lucide-react';
import HeroPipeline from './HeroPipeline';
import type { BetaSignupSource } from '../../lib/betaSignup';
import { useScrollReveal } from '../../hooks/useScrollReveal';
import { TIERS, bulletText, bulletHighlighted, type Tier } from './pricingData';
import { LANDING_SECTIONS } from './landingSections';
import BetaSignupModal from './BetaSignupModal';

// ─── Stats & Helper ───────────────────────────────────────────────────────────

const CALENDLY_URL = 'https://calendly.com/contact-revertai?hide_event_type_details=1&hide_gdpr_banner=1&background_color=020617&text_color=ffffff&primary_color=f59e0b';

// Mise en relief des termes que l’œil doit accrocher dans une description.
function B({ children }: { children: React.ReactNode }) {
  return <span className="font-semibold text-slate-200">{children}</span>;
}

// ─── Trois problèmes, trois réponses ──

const PROBLEMS = [
  {
    problem: 'Vos CV sont partout, et nulle part.',
    detail:
      'Un Drive, un dossier partagé, une hot list Excel, des notes dans un coin du CRM. Personne ne sait plus qui sait faire quoi, alors on repropose les trois profils dont on se souvient.',
    answer:
      'RevertAI lit chaque CV et en produit une fiche au même format pour tous : compétences, séniorité, expérience, TJM, disponibilité. De quoi qualifier un profil et le défendre devant un client.',
  },
  {
    problem: 'Un appel d’offres, c’est dense.',
    detail:
      'Contraintes contractuelles, stack technique, TJM plafonné, présentiel, certifications. Tout est noyé dans plusieurs pages, et le critère qui bloque se découvre après le refus du client.',
    answer:
      'RevertAI structure l’appel d’offres et vous aide à y voir clair : deal breakers, critères MUST/NICE, TJM, séniorité, mode de travail. Vous savez ce qui compte avant de commencer à chercher.',
  },
  {
    problem: 'Le commercial ne peut pas défendre la technique.',
    detail:
      'Il connaît le client, pas le détail du stack. En rendez-vous, il présente un nom et un CV pendant que trois autres ESN argumentent point par point.',
    answer:
      'Chaque recommandation arrive avec son score, les critères couverts et un argumentaire prêt à envoyer : résumé exécutif, réalisations clés, et pourquoi ce profil répond au besoin.',
  },];

// ─── How it works data ───────────────────────────────────────────────────────

// `key` sert de cle React : la description est du JSX, elle ne peut pas en tenir lieu.
const STEPS = [
  {
    key: 'cv',
    title: 'Vous versez vos CV',
    description: (
      <>
        Un dossier entier, ou votre <B>Google Drive</B>. RevertAI lit chaque fichier et en produit une
        fiche exploitable. Vous n’ouvrez aucun PDF.
      </>
    ),
  },
  {
    key: 'ao',
    title: 'Vous collez l’appel d’offres',
    description: (
      <>
        RevertAI en sort les <B>deal breakers</B>, les critères <B>MUST/NICE</B>, le <B>TJM</B>, la
        <B> séniorité</B> et le <B>mode de travail</B> — avant que vous cherchiez le moindre profil.
      </>
    ),
  },
  {
    key: 'analyse',
    title: 'Vous lancez l’analyse',
    description: (
      <>
        Vous réglez le <B>niveau d’exigence</B> et le nombre de profils à comparer. À l’arrivée : une
        <B> shortlist scorée</B> et une <B>synthèse client</B> prête à envoyer.
      </>
    ),
  },];

// ─── FAQ items ────────────────────────────────────────────────────────────────

const FAQ_ITEMS = [
  {
    question: 'RevertAI remplace-t-il notre ATS ou CRM ?',
    answer:
      "Non. RevertAI complète vos outils existants avec une couche de staffing centrée sur le vivier, les missions et les recommandations explicables.",
  },
  {
    question: 'Faut-il intégrer notre ATS actuel ?',
    answer:
      'Non pour démarrer. Vous pouvez tester un premier cas avec un vivier et quelques missions avant de décider d’une intégration plus poussée.',
  },
  {
    question: 'Peut-on comprendre pourquoi un profil est recommandé ?',
    answer:
      'Oui. L’objectif est de montrer les correspondances, les écarts et les points à vérifier, pas seulement un score.',
  },
  {
    question: 'Que faire si nos données talents ne sont pas parfaitement à jour ?',
    answer:
      'Vous pouvez commencer avec les données disponibles, puis enrichir progressivement les fiches talents, les disponibilités et les retours clients.',
  },
  {
    question: 'Comment RevertAI traite-t-il les données CV et missions ?',
    answer:
      'RevertAI est conçu pour traiter des données talents et missions avec isolation par organisation. Les détails juridiques restent cadrés par la politique de confidentialité.',
  },
  {
    question: "À qui s'adresse la bêta ?",
    answer:
      "La bêta s'adresse aux ESN, cabinets IT et équipes staffing qui veulent mieux exploiter leur vivier interne et fiabiliser leurs positionnements clients.",
  },
];

// ─── Pricing helpers ──────────────────────────────────────────────────────────

function PriceDark({ tier }: { tier: Tier }) {
  if (tier.monthly === null) {
    return <div className="font-display text-3xl font-extrabold text-white tracking-tight">Sur devis</div>;
  }
  if (tier.monthly === 0) {
    return <div className="font-display text-4xl font-extrabold text-white tracking-tight">Gratuit</div>;
  }
  return (
    <div className="flex items-baseline justify-center gap-1">
      <span className="font-display text-4xl font-extrabold text-white tracking-tight">{tier.monthly} €</span>
      <span className="text-sm text-slate-400">/mois</span>
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────


export default function LandingPage() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const firstRenderRef = useRef(true);
  useScrollReveal();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [betaModalOpen, setBetaModalOpen] = useState(false);

  const [betaMessage, setBetaMessage] = useState('');
  const [betaSource, setBetaSource] = useState<BetaSignupSource>('landing');

  const openBetaModal = (opts?: { message?: string; source?: BetaSignupSource }) => {
    setBetaMessage(opts?.message ?? '');
    setBetaSource(opts?.source ?? 'landing');
    setBetaModalOpen(true);
  };

  // Handle Supabase auth redirects landing on the landing page
  useEffect(() => {
    const hash = window.location.hash.slice(1);
    if (!hash) return;

    // Arrivée depuis /login, /signup ou /onboarding : ces pages renvoient vers
    // `revertai.io/#beta`. Le formulaire n'est plus dans la page mais dans une
    // modale, donc le hash l'ouvre au lieu de faire défiler.
    if (hash === 'beta') {
      setBetaModalOpen(true);
      return;
    }

    const params = new URLSearchParams(hash);
    const error = params.get('error');
    const accessToken = params.get('access_token');

    if (error) {
      const errorCode = params.get('error_code');
      const msg = errorCode === 'otp_expired'
        ? 'Le lien de confirmation a expiré. Veuillez vous reconnecter pour en recevoir un nouveau.'
        : params.get('error_description')?.replace(/\+/g, ' ') ?? 'Une erreur est survenue.';
      navigate(`/login?auth_error=${encodeURIComponent(msg)}`, { replace: true });
    } else if (accessToken) {
      // Confirmation email landed here instead of app.revertai.io — forward with tokens
      window.location.href = `${APP_ORIGIN}/onboarding#${hash}`;
    }
  }, [navigate]);

  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null);

  const openCalendly = (e: React.MouseEvent) => {
    e.preventDefault();
    if ((window as any).Calendly) {
      (window as any).Calendly.initPopupWidget({
        url: CALENDLY_URL,
      });
      return;
    }
    window.open(CALENDLY_URL, '_blank', 'noopener,noreferrer');
  };

  // Chaque section de la landing a sa propre URL (`/tarifs`, `/constat`…). La page
  // rendue est la même ; c'est la position de défilement qui change. Cet effet est
  // donc le seul endroit qui traduit une URL en position, aussi bien au clic dans
  // le menu qu'à l'ouverture directe d'un lien partagé.
  useEffect(() => {
    const section = LANDING_SECTIONS.find(s => s.path === pathname);
    const isFirst = firstRenderRef.current;
    firstRenderRef.current = false;

    // Au tout premier rendu on saute sans animation : dérouler la page sous les yeux
    // de quelqu'un qui a collé `/tarifs` n'a pas de sens. Ensuite, c'est une
    // navigation dans la page, et le défilement doux montre d'où l'on vient.
    const behavior: 'auto' | 'smooth' = isFirst ? 'auto' : 'smooth';

    if (!section) {
      // Retour sur `/`. Au premier rendu on ne touche à rien, pour laisser au
      // navigateur sa restauration de position (retour arrière, rechargement).
      if (!isFirst) window.scrollTo({ top: 0, behavior });
      return;
    }

    // `requestAnimationFrame` : laisse la mise en page se poser avant de viser
    // l'ancre, sinon on vise une position que le rendu va déplacer.
    const raf = window.requestAnimationFrame(() => {
      document.getElementById(section.id)?.scrollIntoView({ behavior });
    });
    return () => window.cancelAnimationFrame(raf);
  }, [pathname]);

  return (
    <div className="min-h-screen bg-slate-950 text-white">

      {/* ── Section 1 — Header ── */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-slate-950/80 backdrop-blur-md border-b border-slate-800/60">
        <div className="max-w-6xl 2xl:max-w-[100rem] mx-auto px-6 h-16 flex items-center justify-between">

          {/* Logo */}
          <div className="flex items-center gap-2">
            <img src={logoUrl} alt="RevertAI Logo" className="h-8 w-auto object-contain" />
            <span className="text-xs font-bold tracking-tight text-white">
              Revert<span className="text-amber-500">AI</span>
            </span>
          </div>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-8">
            <Link to="/produit" className="text-xs text-slate-400 hover:text-white transition-colors">
              Produit
            </Link>
            <Link to="/constat" className="text-xs text-slate-400 hover:text-white transition-colors">
              Constat
            </Link>
            <Link to="/fonctionnement" className="text-xs text-slate-400 hover:text-white transition-colors">
              Fonctionnement
            </Link>
            <Link to="/tarifs" className="text-xs text-slate-400 hover:text-white transition-colors">
              Tarifs
            </Link>
            <Link to="/faq" className="text-xs text-slate-400 hover:text-white transition-colors">
              FAQ
            </Link>
          </nav>

          {/* Desktop CTAs */}
          <div className="hidden md:flex items-center gap-4">
            <button
              onClick={openCalendly}
              className="text-xs text-amber-500 border border-amber-500/30 px-3 py-3 rounded-md hover:text-amber-400 transition-colors flex items-center gap-2"
            >
              Démo en direct <Sparkles size={14} className="animate-pulse" />
            </button>
            <Link to="/login" className="text-xs text-slate-300 hover:text-white border border-slate-700 hover:border-slate-500 px-3 py-3 rounded-md transition-colors">
              Se connecter
            </Link>
            <button
              onClick={() => openBetaModal()}
              className="bg-amber-600 hover:bg-amber-500 text-white text-xs font-bold px-4 py-3 rounded-lg transition-colors"
            >
              Rejoindre la bêta
            </button>
          </div>

          {/* Mobile toggle */}
          <button
            className="md:hidden text-slate-400 hover:text-white transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Menu"
          >
            {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-slate-900 border-t border-slate-800 px-6 py-5 space-y-4">
            <button
              onClick={() => {
                setMobileMenuOpen(false);
                openBetaModal();
              }}
              className="block w-full bg-amber-600 hover:bg-amber-500 text-white text-xs font-bold px-4 py-3 rounded-lg text-center transition-colors"
            >
              Rejoindre la bêta
            </button>
            <button
              onClick={e => {
                setMobileMenuOpen(false);
                openCalendly(e);
              }}
              className="block w-full text-left text-xs text-amber-500 hover:text-amber-400 transition-colors flex items-center gap-2"
            >
              Démo en direct <Sparkles size={14} />
            </button>
            <Link
              to="/produit"
              className="block text-xs text-slate-400 hover:text-white transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              Produit
            </Link>
            <Link
              to="/fonctionnement"
              className="block text-xs text-slate-400 hover:text-white transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              Fonctionnement
            </Link>
            <Link
              to="/tarifs"
              className="block text-xs text-slate-400 hover:text-white transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              Tarifs
            </Link>
            <Link
              to="/faq"
              className="block text-xs text-slate-400 hover:text-white transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              FAQ
            </Link>
            <Link
              to="/login"
              className="block text-xs text-slate-300 hover:text-white transition-colors"
              onClick={() => setMobileMenuOpen(false)}
            >
              Se connecter
            </Link>
          </div>
        )}
      </header>

      {/* ── Section 2 — Hero + Mockup ── */}
      <section className="relative pt-28 pb-16 px-6 overflow-hidden">
        {/* Ambient glow */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[520px] bg-amber-600/10 rounded-full blur-3xl pointer-events-none" />

        <div className="relative max-w-6xl 2xl:max-w-[100rem] mx-auto">
          {/* Pipeline : les trois promesses a gauche, l ecran qui les prouve a droite.
              L ancre #features vise ce bloc car c est desormais la presentation produit. */}
          <div id="fonctionnalites" className="scroll-mt-28">
            <HeroPipeline onImageClick={setSelectedImage} />
          </div>
        </div>
      </section>

      {/* ── Section 3 — Trois problèmes, trois réponses ── */}
      <section id="constat" className="py-24 px-6 border-t border-slate-800/60 scroll-mt-20">
        <div className="max-w-6xl 2xl:max-w-[100rem] mx-auto">
          <div className="mb-16" data-reveal>
            <span className="font-mono-ui mb-5 flex items-center gap-3 text-xs font-medium uppercase tracking-[0.2em] text-amber-500">
              <span className="draw-rule h-px w-8 bg-amber-500/50" aria-hidden="true" />
              01
            </span>
            <h2 className="font-display text-3xl md:text-4xl 2xl:text-5xl font-bold tracking-tight lg:whitespace-nowrap">
              Trois choses vous coûtent des positionnements.
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {PROBLEMS.map((item, i) => (
              <div
                key={item.problem}
                data-reveal
                style={{ ['--reveal-delay' as string]: `${i * 110}ms` }}
                className="group flex flex-col overflow-hidden rounded-2xl border border-slate-800 bg-slate-900/70 transition-all duration-300 hover:-translate-y-2 hover:scale-[1.02] hover:border-amber-500/30 hover:bg-slate-900 hover:shadow-2xl hover:shadow-amber-950/30"
              >
                {/* Le constat */}
                <div className="flex-1 p-6">
                  <h3 className="font-display mb-3 text-lg font-bold leading-snug text-white">{item.problem}</h3>
                  <p className="text-sm leading-relaxed text-slate-400">{item.detail}</p>
                </div>
                {/* Ce que RevertAI y oppose */}
                <div className="border-t border-slate-800 bg-amber-950/20 p-6 transition-colors duration-300 group-hover:bg-amber-950/40">
                  <p className="mb-2 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-amber-500">
                    <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" />
                    Avec RevertAI
                  </p>
                  <p className="text-sm leading-relaxed text-slate-300">{item.answer}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Section 4 — Comment ça marche ── */}
      <section id="fonctionnement" className="py-24 px-6 border-t border-slate-800/60 scroll-mt-20">
        <div className="max-w-6xl 2xl:max-w-[100rem] mx-auto">
          <div className="mb-16" data-reveal>
            <span className="font-mono-ui mb-5 flex items-center gap-3 text-xs font-medium uppercase tracking-[0.2em] text-amber-500">
              <span className="draw-rule h-px w-8 bg-amber-500/50" aria-hidden="true" />
              02
            </span>
            <h2 className="font-display text-3xl md:text-4xl 2xl:text-5xl font-bold mb-6 tracking-tight">
              Comment ça marche
            </h2>
            <p className="font-display text-2xl md:text-3xl font-bold tracking-tight text-white">
              Trois gestes de votre côté.
              <span className="text-slate-500"> Le reste, RevertAI s’en charge.</span>
            </p>
          </div>

          <div className="grid gap-12 md:grid-cols-3 md:gap-8">
            {STEPS.map((step, i) => (
              <div
                key={step.key}
                data-reveal
                style={{ ['--reveal-delay' as string]: `${i * 130}ms` }}
                className="group text-center"
              >
                <span className="draw-rule mb-8 block h-px w-full bg-slate-800" aria-hidden="true" />
                <span className="font-display mx-auto block text-6xl font-bold leading-none text-slate-700 transition-colors duration-300 group-hover:text-amber-500/70" aria-hidden="true">
                  {String(i + 1).padStart(2, '0')}
                </span>
                <h3 className="font-display mt-6 mb-3 text-xl font-bold tracking-tight text-white">{step.title}</h3>
                <p className="mx-auto max-w-xs text-sm leading-relaxed text-slate-400">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Section 5 — Tarifs ── */}
      <section id="tarifs" className="py-24 px-6 border-t border-slate-800/60 relative overflow-hidden scroll-mt-20">
        {/* Ambient glow */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-amber-600/5 rounded-full blur-3xl pointer-events-none" />

        <div className="relative max-w-6xl 2xl:max-w-[100rem] mx-auto">
          <div className="mb-14" data-reveal>
            <span className="font-mono-ui mb-5 flex items-center gap-3 text-xs font-medium uppercase tracking-[0.2em] text-amber-500">
              <span className="draw-rule h-px w-8 bg-amber-500/50" aria-hidden="true" />
              03
            </span>
            <h2 className="font-display text-3xl md:text-4xl 2xl:text-5xl font-bold tracking-tight">
              Deux offres, ouvertes pendant la bêta.
            </h2>
          </div>

          {/* Cartes : la bêta d’abord, y compris en mobile (ordre du tableau) */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5 items-stretch">
            {TIERS.filter(t => t.status === 'live').map((tier, i) => {
              return (
                <div
                  key={tier.id}
                  data-reveal
                  style={{ ['--reveal-delay' as string]: `${i * 110}ms` }}
                  className={`group relative flex flex-col rounded-3xl p-6 transition-all ${
                    tier.highlight
                      ? 'bg-slate-900 border-2 border-amber-500 shadow-[0_0_40px_rgba(245,158,11,0.15)] md:-translate-y-3'
                      : 'bg-slate-900/60 border border-slate-800 hover:border-slate-700'
                  }`}
                >
                  {tier.badge && (
                    <div
                      className={`absolute -top-3.5 left-1/2 -translate-x-1/2 whitespace-nowrap px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide ${
                        tier.highlight
                          ? 'bg-amber-500 text-white shadow-lg shadow-amber-900/40'
                          : 'bg-slate-800 text-slate-300 border border-slate-700'
                      }`}
                    >
                      {tier.badge}
                    </div>
                  )}

                  <div className="flex flex-col items-center text-center">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${tier.accentBg} ${tier.accent}`}>
                      {tier.icon}
                    </div>
                    <h3 className="font-display mt-3 text-lg font-bold text-white leading-none">{tier.name}</h3>
                    <span className="mt-1 text-xs text-slate-400 font-medium">{tier.tagline}</span>
                  </div>

                  <p className="mt-4 min-h-[44px] flex items-center justify-center text-center text-sm text-slate-400 italic leading-snug">
                    « {tier.promise} »
                  </p>

                  <div className="mt-4 text-center">
                    <PriceDark tier={tier} />
                    {tier.priceNote && <p className="mt-1 text-xs text-slate-400">{tier.priceNote}</p>}
                  </div>

                  {/* Le CTA des cartes « à venir » reste bien vivant : c’est le seul
                      signal d’intention d’achat de la page. Ni disabled, ni aria-disabled. */}
                  <button
                    onClick={() =>
                      openBetaModal({ message: tier.interestMessage, source: 'pricing' })
                    }
                    className={`mt-5 w-full py-2.5 rounded-xl text-sm font-semibold transition-all flex items-center justify-center gap-1.5 ${
                      tier.ctaStyle === 'primary'
                        ? 'bg-amber-600 hover:bg-amber-500 text-white shadow-lg shadow-amber-900/30 active:scale-95'
                        : 'bg-slate-800 hover:bg-slate-700 text-white border border-slate-700'
                    }`}
                  >
                    {tier.cta} <ArrowRight size={15} />
                  </button>

                  {tier.counterpart && (
                    <p className="mt-3 text-center text-xs leading-relaxed text-amber-500/90">{tier.counterpart}</p>
                  )}

                  <div className="mt-6 pt-5 border-t border-slate-800 flex-1">
                    {tier.inheritsFrom && (
                      <p className="text-xs font-semibold text-slate-400 mb-3">
                        Tout <span className="text-slate-200">{tier.inheritsFrom}</span>, plus :
                      </p>
                    )}
                    <ul className="space-y-2.5">
                      {tier.bullets.map((b, bi) => {
                        const strong = bulletHighlighted(b);
                        return (
                          <li
                            key={bi}
                            className={`flex items-start gap-2.5 text-sm ${
                              strong
                                ? '-mx-2.5 rounded-lg px-2.5 py-1.5 font-medium text-amber-300 ring-1 ring-transparent transition-all duration-200 group-hover:bg-amber-500/10 group-hover:ring-amber-500/25'
                                : 'text-slate-300'
                            }`}
                          >
                            {strong ? (
                              <Sparkles size={16} className="shrink-0 mt-0.5 text-amber-400" strokeWidth={2.5} />
                            ) : (
                              <Check size={16} className={`shrink-0 mt-0.5 ${tier.accent}`} strokeWidth={2.5} />
                            )}
                            <span className="leading-snug">{bulletText(b)}</span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </div>
              );
            })}
          </div>

          {/* À venir : annoncés pour l’ancrage de prix — à quoi ça sert, le prix,
              les sièges. Pas de détail de fonctionnalités : rien n’est encore livré. */}
          <div className="mt-6 grid grid-cols-1 gap-5 md:grid-cols-3 items-stretch">
            {TIERS.filter(t => t.status === 'coming_soon').map((tier, i) => (
              <div
                key={tier.id}
                data-reveal
                style={{ ['--reveal-delay' as string]: `${i * 110}ms` }}
                className="relative flex flex-col items-center rounded-2xl border border-slate-800/80 bg-slate-900/40 p-6 text-center transition-colors hover:border-slate-700"
              >
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full border border-slate-700 bg-slate-800 px-3 py-1 text-[10px] font-bold uppercase tracking-wide text-slate-400">
                  {tier.badge}
                </span>

                <div className={`mt-2 flex h-10 w-10 items-center justify-center rounded-xl ${tier.accentBg} ${tier.accent}`}>
                  {tier.icon}
                </div>
                <h3 className="font-display mt-3 text-lg font-bold leading-none text-white">{tier.name}</h3>
                <span className="mt-1 text-xs font-medium text-slate-500">{tier.tagline}</span>

                <p className="mt-4 text-sm leading-snug text-slate-400">{tier.promise}</p>

                <div className="mt-5 flex items-baseline justify-center gap-1.5">
                  {tier.monthly === null ? (
                    <span className="font-display text-2xl font-extrabold tracking-tight text-slate-300">Sur devis</span>
                  ) : (
                    <>
                      <span className="font-display text-2xl font-extrabold tracking-tight text-slate-300">{tier.monthly} €</span>
                      <span className="text-xs text-slate-500">/mois</span>
                    </>
                  )}
                </div>
                <p className="mt-1 text-xs text-slate-500">{tier.priceNote}</p>
              </div>
            ))}
          </div>

          {/* Prise de rendez-vous — globale, volontairement rattachée à aucun forfait :
              c’est la porte d’entrée des deals équipe comme des simples questions. */}
          <div
            className="mt-10 flex flex-col items-center justify-between gap-4 rounded-2xl border border-slate-800 bg-slate-900/40 px-6 py-5 sm:flex-row"
            data-reveal
          >
            <p className="text-sm leading-relaxed text-slate-400">
              Une question, un vivier à reprendre, un besoin d’équipe ? Parlons-en de vive voix.
            </p>
            <button
              onClick={openCalendly}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-xl border border-amber-500/40 px-5 py-2.5 text-sm font-semibold text-amber-500 transition-colors hover:bg-amber-500/5"
            >
              Prendre rendez-vous <ArrowRight size={15} />
            </button>
          </div>
        </div>
      </section>

      {/* ── Section 6 — FAQ ── */}
      <section id="faq" className="py-24 px-6 border-t border-slate-800/60">
        <div className="max-w-3xl mx-auto">
          <div className="mb-12" data-reveal>
            <span className="font-mono-ui mb-5 flex items-center gap-3 text-xs font-medium uppercase tracking-[0.2em] text-amber-500">
              <span className="draw-rule h-px w-8 bg-amber-500/50" aria-hidden="true" />
              04
            </span>
            <h2 className="font-display text-3xl md:text-4xl 2xl:text-5xl font-bold mb-4 tracking-tight">Questions fréquentes</h2>
            <p className="text-slate-400 text-base">
              Les objections que vous avez probablement.
            </p>
          </div>

          <div>
            {FAQ_ITEMS.map((faq, i) => (
              <div key={faq.question} className="border-b border-slate-800">
                <button
                  className="w-full flex items-center justify-between py-5 text-base font-bold text-left text-white hover:text-amber-400 transition-colors"
                  onClick={() => setOpenFaqIndex(openFaqIndex === i ? null : i)}
                  aria-expanded={openFaqIndex === i}
                  aria-controls={`faq-panel-${i}`}
                >
                  {faq.question}
                  <ChevronRight
                    size={18}
                    className={`transition-transform flex-shrink-0 ${openFaqIndex === i ? 'rotate-90' : ''}`}
                  />
                </button>
                <div
                  id={`faq-panel-${i}`}
                  role="region"
                  inert={openFaqIndex !== i}
                  className={`grid transition-all duration-300 ease-out ${
                    openFaqIndex === i ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
                  }`}
                >
                  <div className="overflow-hidden">
                    <p className="pb-5 text-slate-400 text-sm leading-relaxed">{faq.answer}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Section 7 — Footer ── */}
      <footer className="border-t border-slate-800 py-10 px-6">
        <div className="max-w-6xl 2xl:max-w-[100rem] mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          {/* Logo */}
          <div className="flex items-center gap-2">
            <img src={logoUrl} alt="RevertAI Logo" className="h-6 w-auto object-contain" />
            <span className="text-xs font-bold text-white">
              Revert<span className="text-amber-500">AI</span>
            </span>
          </div>

          <p className="text-slate-500 text-xs">© 2026 RevertAI. Tous droits réservés.</p>

          <div className="flex items-center gap-6">
            <Link to="/privacy" className="text-slate-500 hover:text-slate-300 text-xs transition-colors">
              Confidentialité
            </Link>
            <Link to="/cgu" className="text-slate-500 hover:text-slate-300 text-xs transition-colors">
              CGU
            </Link>
            <Link to="/login" className="text-slate-500 hover:text-slate-300 text-xs transition-colors">
              Connexion
            </Link>
          </div>
        </div>
      </footer>

      {/* ── Inscription bêta ── */}
      {/* Monté en permanence : la modale conserve son étape et l'email déjà
          saisi si le visiteur la referme puis la rouvre. */}
      <BetaSignupModal
        open={betaModalOpen}
        onClose={() => setBetaModalOpen(false)}
        source={betaSource}
        initialMessage={betaMessage}
      />

      {/* ── Image Lightbox Modal ── */}
      {selectedImage && (
        <div 
          className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8 bg-slate-900/95 backdrop-blur-sm cursor-pointer animate-in fade-in duration-200"
          onClick={() => setSelectedImage(null)}
        >
          <button 
            className="absolute top-4 right-4 md:top-8 md:right-8 text-white bg-slate-800 hover:bg-slate-700 p-3 rounded-full transition-colors z-10"
            onClick={() => setSelectedImage(null)}
          >
            <X size={24} />
          </button>
          <img 
            src={selectedImage} 
            alt="Enlarged view" 
            className="max-w-full max-h-[90vh] rounded-xl shadow-[0_0_50px_rgba(0,0,0,0.5)] object-contain cursor-default animate-in zoom-in duration-300"
            onClick={(e) => e.stopPropagation()} 
          />
        </div>
      )}

    </div>
  );
}
